// reusable remote picker — chip strip for selecting one or many remotes.
//
// extracted from AggregateFeedView's remote-toggle strip so it can be reused
// by the library view (single-select today, multi-select later).
//
// modes:
//   - "multi": clicking toggles a remote in/out; long-press solos a remote
//              (selects only that one). enforces minimum 1 selected.
//   - "single": clicking selects exactly that remote; long-press is disabled.
//
// the component is presentational; the parent owns the `value` set and
// receives every change via `onChange`. callers also own how remotes are
// loaded — pass them in via `remotes`.
//
// overflow behavior (9b): when the chip strip would exceed the container
// width, the component collapses to a single trigger button that opens a
// flyout panel (desktop) or modal sheet (mobile ≤ 640px). keyboard: arrow
// keys navigate the list, space/enter toggle, esc closes and returns focus.

import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { Portal } from "solid-js/web";
import { Icon, IconNames } from "../icons/registry";
import { Modal } from "../modals/Modal";
import { resolveBlobUrl } from "../../music/services/storage/blobResolver";
import { isOnline, isProbing, probeRemote } from "../../app/services/remotes/remoteHealth";
import { type Remote, isP2PRemote } from "../../app/services/storage/schemas/remote";

export type RemotePickerMode = "single" | "multi";

interface RemotePickerProps {
  remotes: Remote[];
  /** currently-selected remote ids. for `single` mode, treat first entry. */
  value: Set<string>;
  onChange: (next: Set<string>) => void;
  mode?: RemotePickerMode;
  /** when true, non-admin remotes will be visually de-emphasised + disabled.
   *  the parent is responsible for actually filtering admin status into the
   *  remotes list if it wants stricter behaviour. */
  isRemoteAdmin?: (remoteId: string) => boolean;
  /** floating layout = absolute-positioned strip (matches aggregate feed).
   *  inline layout = static flex row (good for header bars). */
  layout?: "floating" | "inline";
  /** extra class for the outer container */
  class?: string;
  /** fires `true` when the user opens the overflow flyout/modal and
   *  `false` when it closes. parents that want to defer expensive work
   *  while the picker is in use (eg. holding off a graph rebuild) can
   *  combine this with pointer/focus tracking on the picker host. the
   *  inline chip strip emits no events here \u2014 use pointer/focus on
   *  the host element for that case. */
  onActiveChange?: (active: boolean) => void;
  /** z-index for the overflow flyout/modal (backdrop gets this value,
   *  the panel itself gets `+1`). defaults to 1200, above every modal's
   *  own overlay in this codebase (they top out at z-1100 - see
   *  AddMediaModal/ImageCarouselModal) since this component is also used
   *  standalone inside those modals. standalone (non-modal) callers like
   *  AggregateFeedView should pass something below the top nav's own
   *  flyout panel (z-1000) instead, so that takes visual priority when
   *  both are open. */
  flyoutZIndex?: number;
}

const LONG_PRESS_MS = 500;

export function RemotePicker(props: RemotePickerProps) {
  const mode = () => props.mode ?? "multi";
  const layout = () => props.layout ?? "inline";
  const flyoutZIndex = () => props.flyoutZIndex ?? 1200;

  // ── overflow / flyout state (9b) ────────────────────────────────────────────
  let wrapperRef: HTMLDivElement | undefined;
  let chipContainerRef: HTMLDivElement | undefined;
  let triggerRef: HTMLButtonElement | undefined;
  // total chip content width measured once on mount; used to detect overflow
  // as the container is resized without needing to re-render the chips.
  let storedChipsWidth = 0;

  const [isOverflowing, setIsOverflowing] = createSignal(false);
  const [flyoutOpen, setFlyoutOpen] = createSignal(false);
  const [flyoutPos, setFlyoutPos] = createSignal<{ top: number; left: number } | null>(null);
  const [focusedIndex, setFocusedIndex] = createSignal(-1);
  const [isMobile, setIsMobile] = createSignal(
    typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
  );

  onMount(() => {
    // track mobile breakpoint
    const mq = window.matchMedia("(max-width: 640px)");
    const mqH = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", mqH);

    // watch wrapper width to detect overflow.
    // +225 so the picker collapses before the nav bar overlaps the chips.
    // re-measures chip content width on every call so async-loaded remotes
    // (getAllRemotes is async) are accounted for after the chips render.
    const checkOverflow = () => {
      if (!wrapperRef) return;
      if (chipContainerRef) {
        const w = chipContainerRef.scrollWidth;
        if (w > 0) storedChipsWidth = w;
      }
      if (storedChipsWidth === 0) return;
      setIsOverflowing(wrapperRef.clientWidth < storedChipsWidth + 225);
    };
    if (wrapperRef) {
      const obs = new ResizeObserver(checkOverflow);
      obs.observe(wrapperRef);
      window.addEventListener("resize", checkOverflow);
      onCleanup(() => {
        obs.disconnect();
        window.removeEventListener("resize", checkOverflow);
        mq.removeEventListener("change", mqH);
      });
    } else {
      onCleanup(() => mq.removeEventListener("change", mqH));
    }

    // re-check overflow when remotes are loaded (getAllRemotes is async;
    // chipContainerRef.scrollWidth is 0 at mount when remotes haven't loaded yet)
    createEffect(() => {
      void props.remotes.length; // track dependency
      queueMicrotask(checkOverflow);
    });
  });

  // ── selection helpers ──────────────────────────────────────────────────────
  // local (charnel-managed) library always sorts first - it's the one
  // destination that's always available and never worth scrolling past.
  const sortedRemotes = createMemo(() =>
    [...props.remotes].sort(
      (a, b) => Number(!!b.is_charnel_managed) - Number(!!a.is_charnel_managed)
    )
  );

  const isActive = (remoteId: string) => props.value.has(remoteId);

  // offline/checking status comes from the same central store the top nav
  // and boot-time sweep use (app/services/remotes/remoteHealth.ts) - never
  // a per-picker probe, so every picker in the app agrees. "checking" wins
  // over a stale "offline" read so the row doesn't flash offline text right
  // before a probe resolves it back online.
  const isChecking = (remoteId: string) => isProbing(remoteId)();
  const isRemoteOffline = (remoteId: string) =>
    !isChecking(remoteId) && isOnline(remoteId)() === false;

  // force a fresh check the moment a remote is picked, rather than trusting
  // a possibly-stale last-known status - probeRemote's own backoff/dedupe
  // (shared with every other caller) keeps this from being a second, wasted
  // probe when one's already in flight.
  const checkOnPick = (remoteId: string) => {
    const remote = sortedRemotes().find((r) => r.remote_id === remoteId);
    if (remote) void probeRemote(remote, { force: true });
  };

  const select = (remoteId: string) => {
    checkOnPick(remoteId);
    if (mode() === "single") {
      props.onChange(new Set([remoteId]));
      // close flyout after single-select pick
      closeFlyout();
      return;
    }
    const next = new Set(props.value);
    if (next.has(remoteId)) {
      // enforce minimum 1
      if (next.size <= 1) return;
      next.delete(remoteId);
    } else {
      next.add(remoteId);
    }
    props.onChange(next);
  };

  const solo = (remoteId: string) => {
    checkOnPick(remoteId);
    props.onChange(new Set([remoteId]));
  };

  const isLocked = (remoteId: string) =>
    props.isRemoteAdmin ? !props.isRemoteAdmin(remoteId) : false;

  // ── flyout helpers ───────────────────────────────────────────────────────
  const closeFlyout = () => {
    setFlyoutOpen(false);
    props.onActiveChange?.(false);
    triggerRef?.focus();
  };

  const openFlyout = () => {
    setFocusedIndex(-1);
    props.onActiveChange?.(true);
    if (triggerRef) {
      const rect = triggerRef.getBoundingClientRect();
      const FLYOUT_WIDTH = 176; // matches min-w-44
      const margin = 8;
      const left = Math.min(rect.left, window.innerWidth - FLYOUT_WIDTH - margin);
      setFlyoutPos({ top: rect.bottom + 4, left: Math.max(margin, left) });
    }
    setFlyoutOpen(true);
  };

  const handleFlyoutKey = (e: KeyboardEvent) => {
    const count = sortedRemotes().length;
    if (e.key === "Escape") {
      e.preventDefault();
      closeFlyout();
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusedIndex((i) => (i + 1) % count);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusedIndex((i) => (i - 1 + count) % count);
    }
  };

  const selectedLabel = () => {
    const names = [...props.value].map(
      (id) => sortedRemotes().find((r) => r.remote_id === id)?.name ?? id
    );
    if (names.length === 0) return "remotes";
    const joined = names.join(", ");
    return joined.length > 22 ? joined.slice(0, 21) + "…" : joined;
  };

  // ── render ──────────────────────────────────────────────────────────────
  // both floating and inline layouts share the same overflow-aware render path
  // so that ResizeObserver / window resize can correctly attach to wrapperRef
  // and chipContainerRef in both cases.

  const outerClass = () =>
    layout() === "floating"
      ? // z-900: below the top nav's own flyout (z-1000) - "floating" layout
        // is only ever used standalone (AggregateFeedView), never inside a
        // modal, so this doesn't need the flyout's higher z-tier.
        `absolute top-[var(--nav-height,42px)] wide:top-0 left-0 right-0 z-[900] bg-transparent pointer-events-none flex items-center py-2 px-4 ${props.class ?? ""}`
      : `relative flex items-center ${props.class ?? ""}`;

  const chipStripClass = () =>
    layout() === "floating"
      ? "flex gap-2 flex-nowrap pointer-events-auto"
      : "flex gap-2 overflow-x-auto scrollbar-hide flex-nowrap py-1";

  return (
    <div ref={wrapperRef} class={outerClass()}>
      {/* spacer pushes chips to the right edge in floating layout */}
      <Show when={layout() === "floating"}>
        <div class="flex-1 shrink-0" />
      </Show>

      {/* chip strip — rendered for overflow measurement; hidden when overflowing */}
      <Show when={!isOverflowing()}>
        <div ref={chipContainerRef} class={chipStripClass()}>
          <For each={sortedRemotes()}>
            {(remote) => (
              <RemoteChip
                remote={remote}
                isActive={isActive(remote.remote_id)}
                isLocked={isLocked(remote.remote_id)}
                isOffline={isRemoteOffline(remote.remote_id)}
                isChecking={isChecking(remote.remote_id)}
                mode={mode()}
                onSelect={() => select(remote.remote_id)}
                onSolo={() => solo(remote.remote_id)}
              />
            )}
          </For>
        </div>
      </Show>

      {/* collapsed trigger — shown when chip strip overflows the container */}
      <Show when={isOverflowing()}>
        <button
          ref={triggerRef}
          type="button"
          onClick={openFlyout}
          class={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-elevated-hover)] hover:text-[var(--color-text-primary)] cursor-pointer shrink-0${layout() === "floating" ? " pointer-events-auto" : ""}`}
          aria-haspopup="listbox"
          aria-expanded={flyoutOpen()}
          title="pick remote"
        >
          {selectedLabel()}
          <Show when={isChecking([...props.value][0] ?? "")}>
            <Icon name="loader" size={12} className="animate-spin" />
          </Show>
          <Icon name="chevronDown" size={12} />
        </button>

        {/* desktop flyout via Portal, anchored to trigger. defaults to
            z-1200 - above any modal's own overlay (modals in this
            codebase top out at z-1100, see AddMediaModal/ImageCarouselModal)
            since this component is used both standalone and inside
            modals - `flyoutZIndex` lets standalone callers (eg.
            AggregateFeedView) opt into something lower instead, so the
            top nav's own flyout (z-1000) still wins over this when both
            are open. */}
        <Show when={!isMobile() && flyoutOpen()}>
          <Portal>
            <div
              class="fixed inset-0"
              style={{ "z-index": flyoutZIndex() }}
              onClick={closeFlyout}
            />
            <div
              class="fixed bg-[var(--color-bg-elevated)] border border-[var(--color-border-subtle)] rounded-lg shadow-xl overflow-y-auto max-h-72 min-w-44 py-1"
              style={{
                "z-index": flyoutZIndex() + 1,
                top: `${flyoutPos()?.top ?? 0}px`,
                left: `${flyoutPos()?.left ?? 0}px`,
              }}
              role="listbox"
              aria-label="select remote"
              tabIndex={-1}
              onKeyDown={handleFlyoutKey}
            >
              <RemoteFlyoutList
                remotes={sortedRemotes()}
                value={props.value}
                mode={mode()}
                focusedIndex={focusedIndex()}
                onSelect={select}
                onSolo={solo}
                isLocked={isLocked}
                isOffline={isRemoteOffline}
                isChecking={isChecking}
              />
            </div>
          </Portal>
        </Show>

        {/* mobile modal sheet - zIndex matches the desktop flyout's, same
            reasoning (must sit above a parent modal like AddMediaModal). */}
        <Show when={isMobile()}>
          <Modal
            isOpen={flyoutOpen()}
            onClose={closeFlyout}
            title="select remote"
            size="sm"
            zIndex={flyoutZIndex()}
          >
            <div class="py-1">
              <RemoteFlyoutList
                remotes={sortedRemotes()}
                value={props.value}
                mode={mode()}
                focusedIndex={focusedIndex()}
                onSelect={select}
                onSolo={solo}
                isLocked={isLocked}
                isOffline={isRemoteOffline}
                isChecking={isChecking}
              />
            </div>
          </Modal>
        </Show>
      </Show>
    </div>
  );
}

// ── RemoteFlyoutList ────────────────────────────────────────────────────────────────
// shared vertical list body used by both the desktop flyout and the mobile modal.
function RemoteFlyoutList(props: {
  remotes: Remote[];
  value: Set<string>;
  mode: RemotePickerMode;
  focusedIndex: number;
  onSelect: (remoteId: string) => void;
  onSolo: (remoteId: string) => void;
  isLocked: (remoteId: string) => boolean;
  isOffline: (remoteId: string) => boolean;
  isChecking: (remoteId: string) => boolean;
}) {
  return (
    <For each={props.remotes}>
      {(remote, i) => {
        const active = () => props.value.has(remote.remote_id);
        const locked = () => props.isLocked(remote.remote_id);
        const offline = () => props.isOffline(remote.remote_id);
        const checking = () => props.isChecking(remote.remote_id);
        const focused = () => i() === props.focusedIndex;

        // long press → solo (multi mode only), mirrors RemoteChip's gesture
        let pressTimer: ReturnType<typeof setTimeout> | undefined;
        let didLongPress = false;
        const startPress = () => {
          didLongPress = false;
          if (props.mode !== "multi") return;
          pressTimer = setTimeout(() => {
            didLongPress = true;
            props.onSolo(remote.remote_id);
          }, LONG_PRESS_MS);
        };
        const endPress = () => clearTimeout(pressTimer);

        // image resolution — same logic as RemoteChip
        const isP2P = () => isP2PRemote(remote);
        const remoteImageUrl = () => {
          const url = remote.image_url;
          if (!url) return null;
          if (
            url.startsWith("http://") ||
            url.startsWith("https://") ||
            url.startsWith("asset://")
          ) {
            return url;
          }
          if (!isP2P() && remote.base_url) return `${remote.base_url}${url}`;
          return null;
        };
        const [resolvedBlob] = createResource(
          () =>
            isP2P() && remote.image_blob_id
              ? { blobId: remote.image_blob_id, remoteId: remote.remote_id }
              : null,
          async (params) => {
            if (!params) return null;
            try {
              return await resolveBlobUrl(params.blobId, params.remoteId);
            } catch {
              return null;
            }
          }
        );
        const imageUrl = () => (isP2P() ? resolvedBlob() : remoteImageUrl());
        const [imgError, setImgError] = createSignal(false);

        return (
          <button
            type="button"
            role="option"
            aria-selected={active()}
            disabled={locked()}
            class="w-full flex items-center gap-2.5 px-2 py-1.5 text-sm text-left border-none bg-transparent"
            classList={{
              "text-[var(--color-accent-500)] bg-[var(--color-accent-500)]/8": active(),
              "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)] cursor-pointer":
                !active() && !locked(),
              "outline outline-1 outline-[var(--color-accent-500)] rounded": focused(),
              "opacity-40 cursor-not-allowed": locked(),
              "opacity-60": !locked() && offline(),
            }}
            onClick={() => {
              if (locked() || didLongPress) return;
              props.onSelect(remote.remote_id);
            }}
            onMouseDown={startPress}
            onMouseUp={endPress}
            onMouseLeave={endPress}
            onTouchStart={startPress}
            onTouchEnd={endPress}
            onContextMenu={(e) => e.preventDefault()}
            tabIndex={focused() ? 0 : -1}
          >
            {/* selection dot — always visible regardless of image */}
            <span
              class={`w-3 h-3 rounded-full border-2 flex-shrink-0 transition-colors ${
                active()
                  ? "border-[var(--color-accent-500)] bg-[var(--color-accent-500)]"
                  : "border-[var(--color-border-default)]"
              }`}
            />
            {/* avatar image when available */}
            <Show when={imageUrl() && !imgError()}>
              <img
                src={imageUrl()!}
                alt=""
                class="w-5 h-5 rounded object-cover flex-shrink-0"
                referrerpolicy="no-referrer"
                onError={() => setImgError(true)}
              />
            </Show>
            <span class="truncate" classList={{ "opacity-50": offline() }}>
              {remote.name}
            </span>
            <Show when={remote.is_charnel_managed}>
              <Icon
                name="home"
                size={12}
                color={active() ? "var(--color-accent-500)" : "var(--color-text-muted)"}
              />
            </Show>
            <Show when={checking()}>
              <Icon
                name="loader"
                size={12}
                className="animate-spin"
                color="var(--color-text-muted)"
              />
            </Show>
            <Show when={!checking() && offline()}>
              <span class="text-[10px] text-[var(--color-text-muted)] flex-shrink-0">offline</span>
            </Show>
          </button>
        );
      }}
    </For>
  );
}

interface RemoteChipProps {
  remote: Remote;
  isActive: boolean;
  isLocked: boolean;
  isOffline: boolean;
  isChecking: boolean;
  mode: RemotePickerMode;
  onSelect: () => void;
  onSolo: () => void;
}

function RemoteChip(props: RemoteChipProps) {
  const isP2P = () => isP2PRemote(props.remote);

  const remoteImageUrl = () => {
    const url = props.remote.image_url;
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://") || url.startsWith("asset://")) {
      return url;
    }
    if (!isP2P() && props.remote.base_url) {
      return `${props.remote.base_url}${url}`;
    }
    return null;
  };

  const [resolvedBlob] = createResource(
    () =>
      isP2P() && props.remote.image_blob_id
        ? { blobId: props.remote.image_blob_id, remoteId: props.remote.remote_id }
        : null,
    async (params) => {
      if (!params) return null;
      try {
        return await resolveBlobUrl(params.blobId, params.remoteId);
      } catch {
        return null;
      }
    }
  );

  const imageUrl = () => (isP2P() ? resolvedBlob() : remoteImageUrl());
  const [imgError, setImgError] = createSignal(false);

  // long press → solo (multi mode only)
  let pressTimer: ReturnType<typeof setTimeout> | undefined;
  let didLongPress = false;

  const startPress = (e: Event) => {
    e.stopPropagation();
    didLongPress = false;
    if (props.mode !== "multi") return;
    pressTimer = setTimeout(() => {
      didLongPress = true;
      props.onSolo();
    }, LONG_PRESS_MS);
  };
  const endPress = (e: Event) => {
    e.stopPropagation();
    clearTimeout(pressTimer);
  };
  const handleClick = (e: Event) => {
    e.stopPropagation();
    if (props.isLocked) return;
    if (!didLongPress) {
      props.onSelect();
    }
  };

  const titleText = () => {
    const transport = props.remote.is_charnel_managed ? "" : isP2P() ? "" : " (http)";
    const hint = props.mode === "multi" ? "\nlong press to solo" : "";
    const lock = props.isLocked ? "\nrequires admin" : "";
    const status = props.isChecking ? "\nchecking..." : props.isOffline ? "\noffline" : "";
    return `${props.remote.name}${transport}${hint}${lock}${status}`;
  };

  return (
    <button
      type="button"
      disabled={props.isLocked}
      class={`text-sm rounded-lg transition-all whitespace-nowrap flex items-center justify-center gap-1.5 shrink-0 pointer-events-auto overflow-hidden ${
        imageUrl() && !imgError() ? "pl-0 pr-3 py-0" : "px-3 py-1.5"
      } ${
        props.isActive
          ? "bg-[var(--color-accent-500)] text-[var(--color-text-on-accent)]"
          : "bg-[var(--color-bg-elevated)] text-[var(--color-text-disabled)] hover:bg-[var(--color-bg-elevated-hover)] hover:text-[var(--color-text-secondary)]"
      } ${props.isLocked ? "opacity-50 cursor-not-allowed" : "cursor-pointer"} ${
        !props.isLocked && props.isOffline ? "opacity-60" : ""
      }`}
      onMouseDown={startPress}
      onMouseUp={endPress}
      onMouseLeave={endPress}
      onTouchStart={startPress}
      onTouchEnd={endPress}
      onClick={handleClick}
      onContextMenu={(e) => e.preventDefault()}
      title={titleText()}
      style={{ height: "32px" }}
    >
      <Show when={imageUrl() && !imgError()} fallback={<Icon name={IconNames.recent} size={14} />}>
        <img
          src={imageUrl()!}
          alt=""
          class="h-full rounded-l-lg object-cover flex-shrink-0"
          style={{ width: "auto" }}
          onError={() => setImgError(true)}
        />
      </Show>
      <span>{props.remote.name}</span>
      <Show when={props.remote.is_charnel_managed}>
        <Icon
          name="home"
          size={12}
          color={props.isActive ? "var(--color-text-on-accent)" : "var(--color-text-muted)"}
        />
      </Show>
      <Show when={!isP2P() && !props.remote.is_charnel_managed}>
        <span
          class={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
            props.isActive ? "bg-[var(--color-text-on-accent)]/20" : "bg-blue-600/20 text-blue-400"
          }`}
        >
          http
        </span>
      </Show>
      <Show when={props.isChecking}>
        <Icon
          name="loader"
          size={12}
          className="animate-spin"
          color={props.isActive ? "var(--color-text-on-accent)" : "var(--color-text-muted)"}
        />
      </Show>
      <Show when={!props.isChecking && props.isOffline}>
        <span class="text-[10px] opacity-80">offline</span>
      </Show>
    </button>
  );
}
